/**
 * Flat transaction view for the Sales page — renders every fetched sale as
 * its own row so the 10/25/50 page size maps 1:1 to transactions (not days).
 * The daily-grouped view (SalesDailyTable) remains available as an optional
 * view. Reuses the same search-filter helper and cell semantics.
 */
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, DollarSign, AlertTriangle } from 'lucide-react';
import { cur } from '@/lib/currency';
import { applySearchFilters } from '@/components/AdvancedSearch';
import { ZatcaRowIcon } from '@/components/zatca/ZatcaRowIcon';

export function SalesFlatTable({
  sales,
  branches,
  searchFilters,
  onDeleteSale,
  onViewJournals = () => {},
  onOpenReceiveCredit,
}) {
  const rows = applySearchFilters(
    sales.map((s) => ({ ...s, payment_mode: s.payment_details?.[0]?.mode || 'cash' })),
    searchFilters,
  ).slice().sort((a, b) => new Date(b.date) - new Date(a.date));

  if (rows.length === 0) return null;

  const payChip = (p, i) => (
    <span
      key={i}
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${p.mode === 'cash' ? 'bg-success/10 text-success border-success/30' : p.mode === 'bank' || p.mode === 'card' ? 'bg-info/10 text-info border-info/30' : p.mode === 'online' || p.mode === 'online_platform' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-warning/10 text-warning border-warning/30'}`}
    >{p.mode}: {Number(p.amount || 0).toFixed(0)}</span>
  );

  return (
    <div data-testid="sales-flat-table" className="border rounded-xl overflow-hidden mt-2">
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr className="border-b">
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Date / Time</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Branch</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Type</th>
              <th className="px-3 py-2.5 text-right font-medium text-muted-foreground">Amount</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Payment</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Customer / Notes</th>
              <th className="px-3 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((sale) => {
              const remaining = (sale.credit_amount || 0) - (sale.credit_received || 0);
              const uid = sale.bill_number || sale.order_number || sale.check_number || sale.external_reference;
              return (
                <tr key={sale.id} className="border-b hover:bg-muted/30" data-testid={`sale-flat-row-${sale.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="text-xs font-medium">{sale.date ? format(new Date(sale.date), 'MMM dd, yyyy') : '-'}</div>
                    <div className="text-[10px] text-muted-foreground">{sale.date ? format(new Date(sale.date), 'hh:mm a') : ''}{uid ? ` · #${uid}` : ''}<ZatcaRowIcon status={sale.zatca_status} className="ml-1 align-middle" /></div>
                  </td>
                  <td className="px-3 py-2 text-xs">{branches.find((b) => b.id === sale.branch_id)?.name || '-'}</td>
                  <td className="px-3 py-2"><Badge variant="secondary" className="capitalize text-[10px]">{sale.sale_type}</Badge></td>
                  <td className="px-3 py-2 text-right font-bold text-primary whitespace-nowrap">{cur()} {Number((sale.final_amount != null ? sale.final_amount : sale.amount) || 0).toFixed(2)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 flex-wrap">{(sale.payment_details || []).map(payChip)}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[220px]">{sale.customer_name || sale.platform_name || sale.notes || 'Walk-in'}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      {remaining > 0 && (
                        <Button size="sm" variant="outline" onClick={() => onOpenReceiveCredit(sale)} className="h-7 px-2 text-xs" data-testid={`sale-flat-receive-${sale.id}`}>
                          <DollarSign size={12} className="mr-0.5" />Receive
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onViewJournals(sale.id); }} className="h-7 px-2 text-xs text-info" data-testid={`sale-journals-${sale.id}`}>JE</Button>
                      <Button size="sm" variant="outline" onClick={() => onDeleteSale(sale.id)} className="h-7 px-2 text-error hover:text-error" data-testid={`sale-flat-delete-${sale.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2.5 text-xs text-muted-foreground bg-muted/50 border-t flex items-center gap-1.5">
        <AlertTriangle size={11} className="text-muted-foreground/60" />
        Showing {rows.length.toLocaleString()} transaction{rows.length === 1 ? '' : 's'} on this page — switch to Daily View to group by day.
      </div>
    </div>
  );
}

export default SalesFlatTable;
